<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import axios from "axios";
import { formatDateToWeb } from "@/helpers/date-functions.js";

import { usePermissions } from "@/composables/usePermissions";

export default {
    page: {
        title: "Alternative Devices",
        meta: [{ name: "description", content: appConfig.description }],
    },

    components: {
        Layout,
        PageHeader,
    },

    setup() {
        const permissions = usePermissions();
        return {
            permissions,
        };
    },

    data() {
        return {
            title: "Alternative Devices",
            items: [
                {
                    text: "Device Management",
                    href: "/",
                },
                {
                    text: "Alternative Devices",
                    active: true,
                },
            ],
            selectedStatus: "",
            searchQuery: "",
            statusFilter: "",
            requestTypeFilter: "",
            
            // API state
            loadingList: false,
            loadingCounts: false,
            requests: [],
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0,
            },
            statusCounts: {},

            statusWidgetDefs: [
                {
                    id: "all",
                    key: "",
                    slug: "all",
                    label: "Total Requests",
                    icon: "ri-file-list-3-line",
                    bgClass: "bg-primary-subtle text-primary",
                    avatarBgClass: "bg-primary text-white",
                },
                {
                    id: "awaiting_bu_approval",
                    key: "awaiting_bu_approval",
                    slug: "awaiting_bu_approval",
                    label: "Awaiting BU Approval",
                    icon: "ri-time-line",
                    bgClass: "bg-warning-subtle text-warning",
                    avatarBgClass: "bg-warning text-white",
                },
                {
                    id: "bu_approved",
                    key: "bu_approved",
                    slug: "bu_approved",
                    label: "BU Approved",
                    icon: "ri-checkbox-circle-line",
                    bgClass: "bg-success-subtle text-success",
                    avatarBgClass: "bg-success text-white",
                },
                {
                    id: "awaiting_customer_approval",
                    key: "awaiting_customer_approval",
                    slug: "awaiting_customer_approval",
                    label: "Awaiting Customer",
                    icon: "ri-user-follow-line",
                    bgClass: "bg-info-subtle text-info",
                    avatarBgClass: "bg-info text-white",
                },
            ],
        };
    },

    computed: {
        statusWidgets() {
            const totalAll = Object.values(this.statusCounts).reduce((acc, curr) => acc + (curr.count || 0), 0);
            return this.statusWidgetDefs.map((w) => {
                let count = 0;
                if (w.key === "") {
                    count = totalAll;
                } else {
                    count = this.statusCounts[w.slug]?.count || 0;
                }
                return {
                    ...w,
                    count,
                };
            });
        },
    },

    mounted() {
        this.fetchStatusCounts();
        this.fetchRequests();
    },

    watch: {
        "$route.query.status": {
            immediate: true,
            handler(newStatus) {
                this.selectedStatus = newStatus || "";
                this.pagination.current_page = 1;
                this.fetchRequests();
            },
        },
        statusFilter() {
            this.pagination.current_page = 1;
            this.fetchRequests();
        },
        requestTypeFilter() {
            this.pagination.current_page = 1;
            this.fetchRequests();
        },
    },

    methods: {
        setAuthHeader() {
            const token = localStorage.getItem("jwt");
            if (token) {
                axios.defaults.headers.common["authorization"] = "Bearer " + token;
            }
        },

        async fetchStatusCounts() {
            this.loadingCounts = true;
            try {
                this.setAuthHeader();
                const res = await axios.get(process.env.VUE_APP_API_BASEURL + "request-forms/status-counts");
                if (res.data && res.data.success) {
                    this.statusCounts = res.data.data || {};
                }
            } catch (error) {
                console.error("Failed to fetch status counts:", error);
            } finally {
                this.loadingCounts = false;
            }
        },

        async fetchRequests(page = 1) {
            this.loadingList = true;
            try {
                this.setAuthHeader();
                const params = {
                    page: page,
                    per_page: this.pagination.per_page,
                };

                const statusToFilter = this.statusFilter || this.selectedStatus;
                if (statusToFilter) {
                    params.status = statusToFilter;
                }
                if (this.requestTypeFilter) {
                    params.request_type = this.requestTypeFilter;
                }
                if (this.searchQuery) {
                    params.search = this.searchQuery;
                }

                const res = await axios.get(process.env.VUE_APP_API_BASEURL + "request-forms", { params });
                if (res.data && res.data.success) {
                    const paginated = res.data.data;
                    this.requests = paginated.data || [];
                    this.pagination.current_page = paginated.current_page || 1;
                    this.pagination.last_page = paginated.last_page || 1;
                    this.pagination.total = paginated.total || 0;
                } else {
                    this.requests = [];
                }
            } catch (error) {
                console.error("Failed to fetch alternative device requests:", error);
                this.requests = [];
            } finally {
                this.loadingList = false;
            }
        },

        onSearchSubmit() {
            this.pagination.current_page = 1;
            this.fetchRequests();
        },

        resetAllFilters() {
            this.searchQuery = "";
            this.statusFilter = "";
            this.requestTypeFilter = "";
            this.filterByWidgetStatus("");
        },

        filterByWidgetStatus(statusKey) {
            const query = { ...this.$route.query };
            if (statusKey) {
                query.status = statusKey;
            } else {
                delete query.status;
            }
            this.$router.push({ query });
        },

        formatDate(dateStr) {
            if (!dateStr) return "N/A";
            return formatDateToWeb(dateStr);
        },

        formatCurrency(amount) {
            if (!amount && amount !== 0) return "RM 0.00";
            const num = parseFloat(amount);
            return isNaN(num) ? "RM 0.00" : `RM ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },

        getRequestTypeBadgeClass(type) {
            switch (type) {
                case "Color Change":
                    return "bg-info-subtle text-info border border-info-subtle";
                case "Inventory Unavailable":
                    return "bg-warning-subtle text-warning border border-warning-subtle";
                case "Customer Requested Change":
                    return "bg-primary-subtle text-primary border border-primary-subtle";
                default:
                    return "bg-light text-dark border";
            }
        },

        getStatusBadgeClass(statusObj) {
            const slug = typeof statusObj === 'object' && statusObj !== null ? statusObj.slug : String(statusObj || '').toLowerCase();
            switch (slug) {
                case "awaiting_bu_approval":
                    return "bg-warning-subtle text-warning border border-warning-subtle";
                case "bu_approved":
                    return "bg-success-subtle text-success border border-success-subtle";
                case "bu_rejected":
                    return "bg-danger-subtle text-danger border border-danger-subtle";
                case "awaiting_customer_approval":
                    return "bg-info-subtle text-info border border-info-subtle";
                case "customer_approved":
                    return "bg-primary-subtle text-primary border border-primary-subtle";
                case "customer_rejected":
                    return "bg-danger-subtle text-danger border border-danger-subtle";
                default:
                    return "bg-secondary-subtle text-secondary border border-secondary-subtle";
            }
        },

        getStatusLabel(item) {
            if (!item) return "N/A";
            if (item.status && (item.status.name || item.status.status_name)) {
                return item.status.name || item.status.status_name;
            }
            return item.status_name || item.status_slug || "N/A";
        },
    },
};
require("@/assets/css/datatable.css");
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <!-- Status Summary Widgets -->
        <div class="row">
            <template v-if="loadingCounts">
                <div v-for="n in 4" :key="'widget-skel-' + n" class="col-xl-3 col-md-6">
                    <div class="card card-animate widget-card placeholder-glow">
                        <div class="card-body">
                            <div class="d-flex align-items-center">
                                <div class="flex-grow-1">
                                    <span class="placeholder col-8 rounded"></span>
                                </div>
                            </div>
                            <div class="d-flex align-items-end justify-content-between mt-3">
                                <div>
                                    <h4 class="fs-22 fw-semibold mb-0 w-100">
                                        <span class="placeholder col-5 rounded d-inline-block" style="height: 24px;"></span>
                                    </h4>
                                </div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="placeholder col-12 h-100 rounded d-block"></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template v-else>
                <div
                    v-for="widget in statusWidgets"
                    :key="widget.id"
                    class="col-xl-3 col-md-6"
                >
                    <div
                        class="card card-animate cursor-pointer widget-card"
                        :class="{ 'border border-2 border-primary shadow-lg': selectedStatus === widget.key }"
                        @click="filterByWidgetStatus(widget.key)"
                        role="button"
                    >
                        <div class="card-body">
                            <div class="d-flex align-items-center">
                                <div class="flex-grow-1 overflow-hidden">
                                    <p class="text-uppercase fw-semibold text-muted text-truncate mb-0">
                                        {{ widget.label }}
                                    </p>
                                </div>
                            </div>

                            <div class="d-flex align-items-end justify-content-between mt-3">
                                <div>
                                    <h4 class="fs-22 fw-semibold ff-secondary mb-0">
                                        <span>{{ widget.count }}</span>
                                    </h4>
                                </div>
                                <div class="avatar-sm flex-shrink-0">
                                    <span class="avatar-title rounded fs-3" :class="widget.avatarBgClass">
                                        <i :class="widget.icon"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- Listing Table Card -->
        <div class="row">
            <div class="col-12">
                <div class="card" id="alternativeDevicesList">
                    <div class="card-header border-bottom-dashed">
                        <div class="row g-4 align-items-center">
                            <div class="col-sm">
                                <div>
                                    <h5 class="card-title mb-0">
                                        <i class="ri-file-list-line align-middle me-2 text-primary"></i>Alternative Devices Request List
                                    </h5>
                                </div>
                            </div>
                            <div class="col-sm-auto">
                                <div class="d-flex flex-wrap align-items-start gap-2">
                                    <router-link
                                        to="/device/alternative-devices/create"
                                        class="btn btn-success add-btn"
                                        :class="{ 'disabled': !permissions.canRequestAlternativeDevices?.value }"
                                        :aria-disabled="!permissions.canRequestAlternativeDevices?.value"
                                    >
                                        <i class="ri-add-line align-bottom me-1"></i> New Request
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Filters Bar -->
                    <div class="card-body border-bottom-dashed border-bottom">
                        <form @submit.prevent="onSearchSubmit">
                            <div class="row g-3 align-items-center">
                                <div class="col-xl-3 col-md-3">
                                    <div class="search-box">
                                        <input
                                            type="text"
                                            class="form-control search"
                                            placeholder="Search Request No / Order ID / Device / Creator..."
                                            v-model="searchQuery"
                                            @keyup.enter="onSearchSubmit"
                                        />
                                        <i class="ri-search-line search-icon"></i>
                                    </div>
                                </div>

                                <div class="col-xl-3 col-md-3">
                                    <select class="form-select" v-model="requestTypeFilter">
                                        <option value="">All Request Types</option>
                                        <option value="Color Change">Color Change</option>
                                        <option value="Inventory Unavailable">Inventory Unavailable</option>
                                        <option value="Customer Requested Change">Customer Requested Change</option>
                                    </select>
                                </div>

                                <div class="col-xl-2 col-md-2">
                                    <select class="form-select" v-model="statusFilter">
                                        <option value="">All Statuses</option>
                                        <option value="awaiting_bu_approval">Awaiting BU Approval</option>
                                        <option value="bu_approved">BU Approved</option>
                                        <option value="bu_rejected">BU Rejected</option>
                                        <option value="awaiting_customer_approval">Awaiting Customer Approval</option>
                                        <option value="customer_approved">Customer Approved</option>
                                        <option value="customer_rejected">Customer Rejected</option>
                                    </select>
                                </div>
                                <div class="col-xl-2 col-md-2 text-end">
                                    <button
                                        type="button"
                                        class="btn btn-soft-secondary w-100"
                                        @click="resetAllFilters"
                                        v-if="selectedStatus || statusFilter || requestTypeFilter || searchQuery"
                                    >
                                        <i class="ri-refresh-line me-1 align-bottom"></i> Reset
                                    </button>
                                </div>

                                <div class="col-xl-2 col-md-2 text-end">                                    
                                    <button
                                        type="submit"
                                        class="btn btn-primary w-100"
                                    >
                                    Submit
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <!-- Table -->
                    <div class="card-body">
                        <div class="table-responsive table-card mb-1">
                            <table class="table align-middle table-nowrap mb-0" id="alternativeDeviceTable">
                                <thead class="table-light text-muted">
                                    <tr>
                                        <th scope="col" style="width: 50px;">No.</th>
                                        <th scope="col">Request &amp; Order Details</th>
                                        <th scope="col">Original Device</th>
                                        <th scope="col">Requested Alternative</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Created By &amp; Date</th>
                                        <th scope="col" class="text-end">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="loadingList">
                                        <td colspan="7" class="text-center py-5">
                                            <div class="spinner-border text-primary me-2" role="status"></div>
                                            <span class="text-muted">Loading requests data...</span>
                                        </td>
                                    </tr>
                                    <tr v-else-if="requests.length === 0">
                                        <td colspan="7" class="text-center py-5">
                                            <div class="text-muted">
                                                <i class="ri-search-line fs-36 text-muted mb-2 d-block"></i>
                                                <h5>No Alternative Device Requests Found</h5>
                                                <p class="mb-0 fs-13">Try adjusting your search query or status filter.</p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-else v-for="(item, index) in requests" :key="item.id">
                                        <td class="fw-medium text-muted">
                                            {{ (pagination.current_page - 1) * pagination.per_page + index + 1 }}
                                        </td>
                                        <td>
                                            <!-- Line 1: Request Number -->
                                            <div class="fw-bold text-dark fs-13">{{ item.request_number }}</div>
                                            <!-- Line 2: Order Link -->
                                            <div class="mt-1" v-if="item.order">
                                                <router-link
                                                    :to="'/orders/customer-order-details/' + (item.order.customer_id || item.order.customer?.id)"
                                                    target="_blank"
                                                    class="fw-medium text-primary fs-12"
                                                >
                                                    <i class="ri-external-link-line me-1 align-middle fs-11"></i>{{ item.order.orderId || ('Order #' + item.order.id) }}
                                                </router-link>
                                            </div>
                                            <!-- Line 3: Request Type Badge -->
                                            <div class="mt-1">
                                                <span :class="['badge px-2 py-0.5 fs-11', getRequestTypeBadgeClass(item.request_type)]">
                                                    {{ item.request_type }}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div v-if="item.current_device || item.currentDevice">
                                                <!-- Line 1: Product Name -->
                                                <div class="fw-semibold text-dark fs-13">
                                                    {{ (item.current_device?.product_name || item.current_device?.product?.product_name || item.currentDevice?.product?.product_name) || (item.current_device?.sku || item.currentDevice?.sku || 'N/A') }}
                                                </div>
                                                <!-- Line 2: SKU -->
                                                <div class="mt-1">
                                                    <span class="badge bg-light text-muted border fs-11">
                                                        SKU: {{ item.current_device?.sku || item.currentDevice?.sku }}
                                                    </span>
                                                </div>
                                                <!-- Line 3: Monthly price + device cost -->
                                                <div class="fs-11 text-muted mt-1">
                                                    <span class="text-danger fw-semibold" v-if="item.current_device?.price || item.currentDevice?.price">
                                                        {{ formatCurrency(item.current_device?.price || item.currentDevice?.price) }}/mo
                                                    </span>
                                                    <!-- <span v-if="(item.current_device?.price || item.currentDevice?.price) && (item.current_device?.device_cost || item.currentDevice?.device_cost)" class="mx-1">•</span>
                                                    <span v-if="item.current_device?.device_cost || item.currentDevice?.device_cost" class="fw-medium">
                                                        Cost: {{ formatCurrency(item.current_device?.device_cost || item.currentDevice?.device_cost) }}
                                                    </span> -->
                                                </div>
                                            </div>
                                            <span v-else class="text-muted">N/A</span>
                                        </td>
                                        <td>
                                            <div v-if="item.new_device || item.newDevice">
                                                <!-- Line 1: Product Name -->
                                                <div class="fw-bold text-success fs-13">
                                                    {{ (item.new_device?.product_name || item.new_device?.product?.product_name || item.newDevice?.product?.product_name) || (item.new_device?.sku || item.newDevice?.sku || 'N/A') }}
                                                </div>
                                                <!-- Line 2: SKU -->
                                                <div class="mt-1">
                                                    <span class="badge bg-success-subtle text-success border border-success-subtle fs-11">
                                                        SKU: {{ item.new_device?.sku || item.newDevice?.sku }}
                                                    </span>
                                                </div>
                                                <!-- Line 3: Monthly price + device cost -->
                                                <div class="fs-11 text-muted mt-1">
                                                    <span class="text-success fw-semibold" v-if="item.new_device?.price || item.newDevice?.price">
                                                        {{ formatCurrency(item.new_device?.price || item.newDevice?.price) }}/mo
                                                    </span>
                                                    <!-- <span v-if="(item.new_device?.price || item.newDevice?.price) && (item.new_device?.device_cost || item.newDevice?.device_cost)" class="mx-1">•</span>
                                                    <span v-if="item.new_device?.device_cost || item.newDevice?.device_cost" class="fw-medium">
                                                        Cost: {{ formatCurrency(item.new_device?.device_cost || item.newDevice?.device_cost) }}
                                                    </span> -->
                                                </div>
                                            </div>
                                            <span v-else class="text-muted">N/A</span>
                                        </td>
                                        <td>
                                            <span :class="['badge px-2.5 py-1 fs-12', getStatusBadgeClass(item.status)]">
                                                {{ getStatusLabel(item) }}
                                            </span>
                                        </td>
                                        <td>
                                            <div>
                                                <span class="fw-semibold text-dark d-block fs-13">
                                                    <i class="ri-user-3-line me-1 text-muted"></i>{{ (typeof item.created_by === 'object' ? item.created_by?.name : item.created_by) || item.creator?.name || 'CS Agent' }}
                                                </span>
                                                <small class="text-muted fs-11">
                                                    <i class="ri-time-line me-1"></i>{{ formatDate(item.created_at) }}
                                                </small>
                                            </div>
                                        </td>
                                        <td class="text-end">
                                            <router-link :to="'/device/alternative-devices/details/' + item.id" class="btn btn-soft-primary btn-sm">
                                                <i class="ri-eye-fill align-bottom me-1"></i> View
                                            </router-link>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Pagination Footer -->
                        <div class="row align-items-center mt-3 g-3" v-if="pagination.total > 0">
                            <div class="col-sm">
                                <div class="text-muted fs-13">
                                    Showing <span class="fw-semibold">{{ (pagination.current_page - 1) * pagination.per_page + 1 }}</span> to
                                    <span class="fw-semibold">{{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}</span> of
                                    <span class="fw-semibold">{{ pagination.total }}</span> results
                                </div>
                            </div>
                            <div class="col-sm-auto" v-if="pagination.last_page > 1">
                                <ul class="pagination pagination-separated pagination-sm justify-content-center justify-content-sm-end mb-0">
                                    <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                        <a href="javascript:void(0);" class="page-link" @click="fetchRequests(pagination.current_page - 1)">Previous</a>
                                    </li>
                                    <li
                                        v-for="p in pagination.last_page"
                                        :key="p"
                                        class="page-item"
                                        :class="{ active: pagination.current_page === p }"
                                    >
                                        <a href="javascript:void(0);" class="page-link" @click="fetchRequests(p)">{{ p }}</a>
                                    </li>
                                    <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                                        <a href="javascript:void(0);" class="page-link" @click="fetchRequests(pagination.current_page + 1)">Next</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
.widget-card {
    transition: all 0.2s ease-in-out;
}
.widget-card:hover {
    transform: translateY(-3px);
}
.bg-purple-subtle {
    background-color: rgba(111, 66, 193, 0.12) !important;
    color: #6f42c1 !important;
}
.border-purple-subtle {
    border-color: rgba(111, 66, 193, 0.25) !important;
}
.border-purple {
    border-color: #6f42c1 !important;
}
.text-purple {
    color: #6f42c1 !important;
}
.badge-rejected {
    background-color: rgba(240, 101, 101, 0.15) !important;
    color: #f06565 !important;
}
</style>
